import { useEffect, useState } from 'react';
import { useOnboardingStepsAtom } from '../../atoms';
import { useIsFetching, useIsMutating } from 'react-query';
import {
  APP_ROUTES,
  HSA_ONBOARD_STEPS,
  RECALCULATE_HSA_STEPS,
} from '../../constants';
import {
  Outlet,
  useLocation,
  useNavigate,
  useNavigationType,
} from 'react-router-dom';

// Components
import OnboardingProgress from './ProgressSideBar';
import ConfirmationModal from '../../components/ConfirmationModal';
import { useIsRecalculatingHSA } from 'atoms/isRecalculatingHSA';
import { isDirtyAtom } from 'atoms/isDirtyAtom';
import { useAtom } from 'jotai';
import { DirtyState } from 'types';

// Keys to Access the isDirtyAtom state - with currentStep as index
const isDirtyKeys = [
  'Account Details',
  'Properties',
  'Brand Signifiers',
  'Whitelisted Domains',
  'Auction Insights File',
] as const;

interface OnboardingProps {
  setTopBarButton: (button: React.ReactNode) => void;
}

function Onboarding({ setTopBarButton }: OnboardingProps) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const navigationType = useNavigationType();

  const isFetching = useIsFetching();
  const isMutating = useIsMutating();

  const [confirmClose, setConfirmClose] = useState(false);
  const { currentStep, setCurrentStep } = useOnboardingStepsAtom();

  const [isRecalculatingHSA, setIsRecalculatingHSA] = useIsRecalculatingHSA();
  const [isDirty] = useAtom(isDirtyAtom);

  const dirtyKey = isDirtyKeys[currentStep - 1];

  const currentPageDataChanged = isDirty[dirtyKey];

  // Bypass confirmation modal for auto save flow (Draft and recalculating HSA)
  // No need to show the confirmation warning as the data is already safely saved - Auto saved
  const bypassConfirmationModal = () => {
    setConfirmClose(false);
    setCurrentStep(0);
    navigate(APP_ROUTES.SETTINGS);
  };

  const autoSaved = !currentPageDataChanged && isRecalculatingHSA;

  useEffect(() => {
    setTopBarButton(
      <button
        onClick={() =>
          autoSaved ? bypassConfirmationModal() : setConfirmClose(true)
        }
      >
        <div className="border border-thirdgray rounded-[20px] px-8 py-1">
          <span className="text-sm font-bold flex items-center text-thirdgray">
            <img
              src={`${process.env.PUBLIC_URL}/static/img/right-arrow.svg`}
              alt="arrow"
              className="mr-[6px] rotate-180"
            />
            CLOSE
          </span>
        </div>
      </button>
    );
    return () => setTopBarButton(null);
  }, [setTopBarButton, autoSaved]);

  useEffect(() => {
    if (
      currentStep >= HSA_ONBOARD_STEPS.length &&
      pathname === APP_ROUTES.ONBOARD_HSA_UPLOAD_SUCCESS
    ) {
      return;
    }

    if (currentStep < 1) {
      if (pathname !== APP_ROUTES.ONBOARD_ACCT_SETUP) {
        navigate(APP_ROUTES.ONBOARD_ACCT_SETUP);
      }
      return;
    }

    // Browser back from Account Details HSA (step 1) — reset to AccountSetup
    if (
      currentStep === 1 &&
      navigationType === 'POP' &&
      pathname !== APP_ROUTES.ONBOARD_ACCT_DETAILS
    ) {
      setCurrentStep(0);
      return;
    }

    if (currentStep === 2 && isRecalculatingHSA) {
      if (pathname !== APP_ROUTES.ONBOARD_HSA_PROPERTIES) {
        navigate(APP_ROUTES.ONBOARD_HSA_PROPERTIES);
      }
    }
    let stepIndex = currentStep - 1;
    if (pathname === APP_ROUTES.ONBOARDING) {
      stepIndex = 0;
    }

    const onboardingStep = HSA_ONBOARD_STEPS[stepIndex] || HSA_ONBOARD_STEPS[0];
    if (pathname !== onboardingStep.path) {
      navigate(onboardingStep.path);
      scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }, [
    currentStep,
    setCurrentStep,
    navigate,
    pathname,
    isRecalculatingHSA,
    navigationType,
  ]);

  // JSX
  return (
    <>
      <div
        className={`min-h-[calc(100vh-140px)] flex flex-col gap-8 px-8 ${
          isMutating || isFetching ? 'cursor-wait' : ''
        }`}
      >
        {(isMutating || isFetching) && currentStep > 0 ? (
          <>
            {/* Backdrop */}
            <div className="fixed inset-0 bg-black/30 z-40 cursor-wait" />

            {/* Centered toast */}
            <div className="fixed inset-y-0 left-0 right-[280px] z-50 flex items-center justify-center">
              <div className="bg-white rounded-lg shadow-xl px-6 py-4 max-w-sm text-center">
                <p className="text-gray-800 font-medium">
                  This may take a moment...
                </p>
              </div>
            </div>
          </>
        ) : null}
        {currentStep > 0 ? (
          <OnboardingProgress
            steps={
              isRecalculatingHSA ? RECALCULATE_HSA_STEPS : HSA_ONBOARD_STEPS
            }
            currentStep={currentStep}
          />
        ) : null}
        <Outlet />
      </div>
      <ConfirmationModal
        isOpen={confirmClose}
        content={
          <div className="text-center px-4">
            <p>Any unsaved changes will be discarded.</p>
            <p>Are you sure you want to continue?</p>
          </div>
        }
        onCancel={() => setConfirmClose(false)}
        onConfirm={() => {
          setCurrentStep(0);
          navigate(APP_ROUTES.SETTINGS);
        }}
      />
    </>
  );
}

export default Onboarding;
